import { create } from 'zustand';
import { desktop } from '../lib/desktop';

/** Document Picture-in-Picture: a real window that stays on top of everything (Chrome and Edge on computers). */
interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}
const pip = (window as Window & { documentPictureInPicture?: DocumentPictureInPicture }).documentPictureInPicture;

/**
 * The desktop app opens its own always-on-top window instead: Electron has the Picture-in-Picture API
 * but shows no window, so older desktop builds get no button at all.
 */
const desktopWindow = desktop?.callWindow === true;
export const canPopOut = desktop ? desktopWindow : pip !== undefined;

interface PopoutState {
  /** The pop-out window while the call is shown there. */
  win: Window | null;
  open(): Promise<void>;
  close(): void;
}

export const usePopout = create<PopoutState>()((set, get) => ({
  win: null,

  /** Must run from a click: browsers only open the window for a user gesture. */
  async open() {
    const current = get().win;
    if (current && !current.closed) return current.focus();
    const win = desktopWindow ? window.open('about:blank', 'novachat-call', 'popup') : await pip?.requestWindow({ width: 520, height: 420 });
    if (!win) return;
    copyStyles(win.document);
    win.document.documentElement.lang = 'tr';
    win.document.title = 'NovaChat · Görüşme';
    win.addEventListener('pagehide', () => set({ win: null }), { once: true });
    set({ win });
  },

  close() {
    get().win?.close();
    set({ win: null });
  },
}));

/** The new window starts blank: bring over the app's styles (and fonts). */
function copyStyles(target: Document) {
  for (const sheet of Array.from(document.styleSheets)) {
    if (sheet.href) {
      const link = target.createElement('link');
      link.rel = 'stylesheet';
      link.href = sheet.href;
      target.head.append(link);
      continue;
    }
    try {
      const style = target.createElement('style');
      style.textContent = Array.from(sheet.cssRules, (rule) => rule.cssText).join('\n');
      target.head.append(style);
    } catch {
      // unreadable sheet without an address: nothing to copy
    }
  }
}
